import css from 'styled-jsx/css';
import { colors, typography } from '../../../../../config/styles';
import _ from '../../../../../utils/LodashImports';

/**
 * SizeButton v2 BEM-based styles
 * Modern styling with enhanced accessibility and responsive design
 */
const SizeButtonStyles = (theme) => {
  const themeColor = _.get(theme, 'color', null) || colors.secondary_color;

  return css`
    /* Block: size-button-v2 */
    .size-button-v2 {
      /* Base styles */
      box-sizing: border-box;
      font-family: inherit;
      font-size: ${typography.sm1};
      font-weight: 600;
      line-height: 1.2;

      /* Dimensions */
      min-width: 48px;
      height: 32px;
      padding: 8px 12px;
      margin: 0 6px 6px 0;

      /* Visual design */
      border: 2px solid ${colors.gray.shade36};
      border-radius: 8px;
      background-color: ${colors.white.shade1};
      color: ${colors.black.shade7};

      /* Interaction */
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      /* Accessibility */
      outline: none;
      position: relative;

      /* Touch optimization */
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      user-select: none;

      /* Responsive typography */
      @media (max-width: 768px) {
        font-size: ${typography.lg1};
        min-width: 48px;
        height: 32px;
        padding: 10px 14px;
      }

      @media (min-width: 1024px) {
        font-size: ${typography.sm1};
        min-width: 48px;
        height: 32px;
        padding: 8px 10px;
      }
    }

    /* Element: size-button-v2__text */
    .size-button-v2__text {
      display: block;
      font-weight: inherit;
      line-height: inherit;
      pointer-events: none;
    }

    /* Modifier: size-button-v2--selected */
    .size-button-v2--selected {
      border-color: ${themeColor};
      background-color: ${colors.white.shade1};
      color: ${colors.black.shade10};
      box-shadow: 0 0 0 1px ${themeColor};
      font-weight: 700;

      /* Enhanced visual feedback */
      transform: translateY(-1px);
    }

    /* Modifier: size-button-v2--unavailable */
    .size-button-v2--unavailable {
      border-color: ${colors.gray.shade50};
      background-color: ${colors.gray.shade05};
      color: ${colors.gray.shade67};
      cursor: not-allowed;

      /* Strikethrough effect */
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 10%;
        right: 10%;
        height: 1px;
        background-color: ${colors.gray.shade67};
        transform: translateY(-50%) rotate(-15deg);
        pointer-events: none;
      }
    }

    /* Modifier: size-button-v2--enhanced-size-selector */
    .size-button-v2--enhanced-size-selector {
      font-size: 12px;
      min-width: 48px;
      height: 32px;
      border-radius: 8px;
      border: 1px solid ${colors.gray.shade36};
      background-color: ${colors.white.shade1};
      margin: 0 4px 4px 0;

      &.size-button-v2--selected {
        border: 1px solid ${colors.black.shade10};
        background-color: ${colors.gray.shade05};
        box-shadow: none;
        transform: none;
      }

      &.size-button-v2--unavailable {
        border: none;
        background-color: ${colors.gray.shade12};

        .size-button-v2__text {
          text-decoration: line-through;
        }

        &::before {
          display: none;
        }
      }
    }

    /* Hover states */
    .size-button-v2:hover:not(:disabled):not(.size-button-v2--unavailable) {
      border: none;
      background-color: ${colors.gray.shade12};
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .size-button-v2--selected:hover:not(:disabled) {
      box-shadow: 0 0 0 1px ${themeColor}, 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    /* Focus states */
    .size-button-v2:focus-visible {
      outline: 2px solid ${themeColor};
      outline-offset: 2px;
    }

    /* Active states */
    .size-button-v2:active:not(:disabled):not(.size-button-v2--unavailable) {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
      .size-button-v2 {
        border-width: 2px;
        font-weight: 700;
      }

      .size-button-v2--selected {
        border-width: 3px;
      }
    }

    /* Reduced motion support */
    @media (prefers-reduced-motion: reduce) {
      .size-button-v2 {
        transition: none;
      }

      .size-button-v2:hover:not(:disabled):not(.size-button-v2--unavailable),
      .size-button-v2--selected:hover:not(:disabled),
      .size-button-v2:active:not(:disabled):not(.size-button-v2--unavailable) {
        transform: none;
      }
    }

    /* Print styles */
    @media print {
      .size-button-v2 {
        border: 1px solid ${colors.black.shade10};
        background: transparent;
        color: ${colors.black.shade10};
        box-shadow: none;
        transform: none;
      }

      .size-button-v2--selected {
        background: ${colors.gray.shade20};
      }
    }
  `;
};

export { SizeButtonStyles };
